/**
 * Distance calculation utilities using the Haversine formula
 * Calculates the great-circle distance between two points on Earth
 */

/**
 * Convert degrees to radians
 */
function toRadians(degrees: number): number {
    return degrees * (Math.PI / 180)
}

/**
 * Calculate distance between two coordinates using Haversine formula
 *
 * @param lat1 - Latitude of first point (in degrees)
 * @param lng1 - Longitude of first point (in degrees)
 * @param lat2 - Latitude of second point (in degrees)
 * @param lng2 - Longitude of second point (in degrees)
 * @returns Distance in miles
 */
export function calculateDistance(
    lat1: number,
    lng1: number,
    lat2: number,
    lng2: number
): number {
    const R = 3959 // Earth's radius in miles (use 6371 for kilometers)

    const dLat = toRadians(lat2 - lat1)
    const dLng = toRadians(lng2 - lng1)

    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRadians(lat1)) *
            Math.cos(toRadians(lat2)) *
            Math.sin(dLng / 2) *
            Math.sin(dLng / 2)

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

    return R * c
}

/**
 * Calculate distance from coordinates (string format) as used in country-state-city package
 *
 * @param lat1 - Latitude of first point (string)
 * @param lng1 - Longitude of first point (string)
 * @param lat2 - Latitude of second point (string)
 * @param lng2 - Longitude of second point (string)
 * @returns Distance in miles, or null if coordinates are invalid
 */
export function calculateDistanceFromStrings(
    lat1: string | undefined,
    lng1: string | undefined,
    lat2: string | undefined,
    lng2: string | undefined
): number | null {
    if (!lat1 || !lng1 || !lat2 || !lng2) {
        return null
    }

    const lat1Num = parseFloat(lat1)
    const lng1Num = parseFloat(lng1)
    const lat2Num = parseFloat(lat2)
    const lng2Num = parseFloat(lng2)

    if (isNaN(lat1Num) || isNaN(lng1Num) || isNaN(lat2Num) || isNaN(lng2Num)) {
        return null
    }

    return calculateDistance(lat1Num, lng1Num, lat2Num, lng2Num)
}

/**
 * Check if a location is within a certain radius of another location
 *
 * @param searchLat - Search location latitude
 * @param searchLng - Search location longitude
 * @param targetLat - Target location latitude
 * @param targetLng - Target location longitude
 * @param radiusMiles - Maximum distance in miles (default: 100)
 * @returns true if within radius, false otherwise
 */
export function isWithinRadius(
    searchLat: string | undefined,
    searchLng: string | undefined,
    targetLat: string | undefined,
    targetLng: string | undefined,
    radiusMiles: number = 100
): boolean {
    const distance = calculateDistanceFromStrings(searchLat, searchLng, targetLat, targetLng)

    if (distance === null) {
        return false
    }

    return distance <= radiusMiles
}

/**
 * Format distance for display
 *
 * @param miles - Distance in miles
 * @returns Formatted string like "12 miles" or "124 miles"
 */
export function formatDistance(miles: number): string {
    return `${Math.round(miles)} miles`
}
